import { useRequest } from 'ahooks'
import { SupportedTicker, TICKERS, TICKERS_MAP_BY_NAME } from '@/constants/token'

type CoinMarketCapData = Record<number, { quote: { USD: { price: number } } }>
type CoinGeckoData = Record<string, { usd: number }>

// TODO: More precise type definition
// type TokenId = {
//   source: Source
//   id: Source extends 'CoinMarketCap' ? number : string
// }

type TokenPrice = Record<number | string, number>

function transformCmcResponse(originalObj: CoinMarketCapData): TokenPrice {
  const transformedObj: TokenPrice = {}

  for (const key in originalObj) {
    if (originalObj.hasOwnProperty(key)) {
      const price = originalObj[key].quote.USD.price
      transformedObj[key] = price
    }
  }

  return transformedObj
}

interface CoinMarketCapPriceResponse {
  data: Record<
    number,
    {
      quote: {
        USD: {
          price: number
        }
      }
    }
  >
}

const fetchPriceFromCoinMarketCap = async (ids: (number | string)[], convert = 'USD'): Promise<TokenPrice> => {
  if (ids.length <= 0) return null

  const resp: CoinMarketCapPriceResponse = await fetch(
    `https://api.multibit.exchange/vendor/api/quotes/latest?id=${ids.join(',')}&convert=${convert}`,
  ).then((res) => res.json())

  return transformCmcResponse(resp.data)
}

function transformCoinGeckoResponse(originalObj: CoinGeckoData): TokenPrice {
  const transformedObj: TokenPrice = {}

  for (const key in originalObj) {
    if (originalObj.hasOwnProperty(key)) {
      const price = originalObj[key].usd
      transformedObj[key] = price
    }
  }

  return transformedObj
}

const fetchPriceFromCoinGecko = async (ids: string[], vsCurrencies = 'usd') => {
  // return Promise.reject('Error')

  if (ids.length <= 0) return null

  const resp: CoinGeckoData = await fetch(
    `https://api.coingecko.com/api/v3/simple/price?ids=${ids.join(',')}&vs_currencies=${vsCurrencies}`,
  ).then((res) => res.json())

  return transformCoinGeckoResponse(resp)
}

export const getTokensPrice = async (tokens: { id: number | string; source: string }[]) => {
  const geckoTokenIds = tokens.filter((t) => t.source === 'CoinGecko').map((t) => String(t.id))
  const marketCapTokenIds = tokens.filter((t) => t.source === 'CoinMarketCap').map((t) => t.id)

  const [coinMarketCapPrice, coinGeckoPrice] = await Promise.allSettled([
    fetchPriceFromCoinMarketCap(marketCapTokenIds),
    fetchPriceFromCoinGecko(geckoTokenIds),
  ]).then((results) =>
    results.map((result) => {
      if (result.status === 'fulfilled') {
        return result.value
      } else {
        return null
      }
    }),
  )

  const priceMap = {
    CoinMarketCap: coinMarketCapPrice,
    CoinGecko: coinGeckoPrice,
  }

  const resp: TokenPrice = {}
  tokens.forEach((token) => {
    const price = priceMap?.[token.source]?.[token.id]

    resp[token.id] = price
  })

  return resp
}

export const useAllTokenPrice = () => {
  return useRequest(() => getTokensPrice(TICKERS.filter((t) => t.priceInfo).map((t) => t.priceInfo)), {
    cacheKey: 'all-tokens-price',
    staleTime: 60 * 1000,
  })
}

export const useTokenPrice = (tickerName: SupportedTicker) => {
  const request = useAllTokenPrice()
  return { ...request, data: request?.data?.[TICKERS_MAP_BY_NAME[tickerName]?.priceInfo?.id] }
}
